<template>
  <div v-if="showPreview" class="opc-preview">
    <zm-alert v-if="showProratedChargeError" type="error" show-icon :closable="false" class="zm-alert__error">
      <p slot="title">{{ $t(`billing.common.update_plan_error`) }}</p>
    </zm-alert>
    <zm-button
      v-if="isInrCitEligible"
      data-testid="checkout-button-330"
      round
      class="text-capitalize opc-preview__update-plan-btn"
      size="large"
      type="primary"
      :disabled="updatePlanDisabled"
      @click="updatePlanClicked"
    >
      {{ $t(`billing.common.update_plan`) }}
    </zm-button>
  </div>
</template>

<script type="text/jsx">
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { PREVIEW_ACTION_TYPES, PREVIEW_API_ACTIONS_ENUM } from '@/store/modules/preview';
import pageFlow from '@/flows';
import { logger } from '@/utils/log';
import { debounce, get } from 'lodash';
import { planTypes, trackingPlanTypes } from '@/common/constants';
import { checkIfPaidToPaidFlow, isAdd2CartFlow } from '@/views/checkout/payment/helper/util';
import { isEmptyVal } from '@/utils';

export default {
  name: 'preview-payment',
  components: {},
  props: {
  },
  data() {
    return {
      showProratedChargeError: false,
      retryCitFlowOnCartUpdate: false,
    }
  },
  computed: {
    ...mapGetters(['inrCitEnabled', 'isInrCitEligible', 'getSelectedPlans', 'isSelectedSameAsEditablePlansInBought', 'isProratedChargeAmountCalculated', 'isLoadingPreview', 'getProratedChargeTotalAmount', 'cartFingerPrint']),
    updatePlanDisabled() {
      return this.isSelectedSameAsEditablePlansInBought || this.isLoadingPreview || (this.isInrCitEligible && this.isProratedChargeAmountCalculated);
    },
    trackingPlanKey() {
      return get(trackingPlanTypes, get(planTypes, this.getSelectedPlans[0]?.type));
    },
    showPreview() {
      return checkIfPaidToPaidFlow(this.$context.flow) && this.inrCitEnabled;
    },
  },
  created() {
    if (this.showPreview) {
      const flowType = isAdd2CartFlow() ? 'add' : 'edit';
      this.checkPreviewEligibility({ flowType }); // send request to define if current flow is eligible for CIT (isInrCitEligible)
    }
  },
  methods: {
    ...mapActions({
      resetProratedCharges: PREVIEW_ACTION_TYPES.resetProratedCharges,
      setProratedCharges: PREVIEW_ACTION_TYPES.setProratedCharges,
      checkPreviewEligibility: PREVIEW_ACTION_TYPES.checkPreviewEligibility,
    }),
    ...mapMutations(['setIsLoadingPreview', 'setIsProratedChargeAmountCalculated', 'setOnSessionPaymentEnabled', 'setIsPreviewEligible', 'setConfigCardLoading']),
    scrollIntoCartView: debounce(() => {
      const node = document.querySelector('.opc-compound__payment');
      if (!node) {
        return;
      }
      node.style['scroll-margin-top'] = '-20px';
      node.scrollIntoView({ behavior: 'smooth' });
    }, 1000),
    async updatePlanClicked() {
      try {
        this.setIsLoadingPreview(true);
        // ZOOM-1043294: not allowed change the amount and billing cycle when preview loading
        this.setConfigCardLoading(true);

        // tracking
        this.$tracking.buyInteraction('update plan', this.trackingPlanKey, 'billing edit plan');

        // Call /opc/plan/update with isPreview=true
        const { eventResult = {} } = (await pageFlow.submit({
          preview: true,
          previewActions: [PREVIEW_API_ACTIONS_ENUM.PRORATED]
        })) || {};
        const previewOrder = JSON.parse(eventResult?.previewOrder);
        if (!previewOrder || isEmptyVal(previewOrder)) {
          throw new Error('Empty response from preview API');
        }

        await this.setProratedCharges(previewOrder);

        if(this.getProratedChargeTotalAmount <= 0){
          this.setIsProratedChargeAmountCalculated(false); // Disable "Proceed to Payment" button
          this.setIsPreviewEligible(false); // Switch back to INR MIT flow
          return;
        }

        // Collapse the plan card and scroll down to Order Summary
        !this.$context.is.desktop && this.$context.$emit("collapse-plan-card");
        !this.$context.is.desktop && this.scrollIntoCartView();
      } catch (e) {
        logger.error('update plan error', e);
        this.$tracking.buyInteraction('update plan', `error - ${e}`, 'billing edit plan');
        this.setIsProratedChargeAmountCalculated(false); // Disable "Proceed to Payment" button
        this.setIsPreviewEligible(false); // Switch back to INR MIT flow
      } finally {
        this.setIsLoadingPreview(false); // Preview API call is complete
        this.setConfigCardLoading(false);
      }
    },
  },
  watch: {
    cartFingerPrint: {
      immediate: true,
      handler() {
        if (this.retryCitFlowOnCartUpdate) {
          this.setIsPreviewEligible(true); // Switch to INR CIT flow
        }
        this.resetProratedCharges();
      },
    },
    isInrCitEligible: {
      handler(val) {
        this.setOnSessionPaymentEnabled(val); // onSession is disabled for MIT flow
        if (val) {
          this.retryCitFlowOnCartUpdate = true; // Retry CIT flow when cart is updated
          this.$tracking.buyInteraction('emandate', `${this.trackingPlanKey} - impression`, 'billing edit plan');
        }
      },
    }
  },
};
</script>


<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-preview {

  .zm-alert__error {
    margin-bottom: 24px;
    color: #80384D;
    background-color: #F3C2D0;
  }

  &__update-plan-btn {
    @include media(mobile, tablet) {
      width: 100%;
    }
  }

}
</style>
